import React, { useState } from 'react';

import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { Button, IconButton } from 'ui-kit';
import { formatRelativeDateTime } from 'utils/dateUtils';

import { DealRole } from '__generated__/globalTypes';
import { BidRow } from './shared/BidRow';
import {
  BidData,
  groupBidsByBuyer,
  getBidDescription,
  formatBidPrice,
  getOwnershipBadge,
} from './shared/bidUtils';

interface SellerBidReviewProps {
  bids: BidData[];
  onSelectBid: (bidId: string) => void;
  onAcceptBid: (bidId: string) => void;
  selectedBidId: string;
  canManageBids: boolean;
}

export const SellerBidReview: React.FC<SellerBidReviewProps> = ({
  bids,
  onSelectBid,
  onAcceptBid,
  selectedBidId,
  canManageBids,
}) => {
  const [expandedBids, setExpandedBids] = useState<Set<string>>(new Set());

  const toggleExpanded = (bidId: string) => {
    const newExpanded = new Set(expandedBids);
    if (newExpanded.has(bidId)) {
      newExpanded.delete(bidId);
    } else {
      newExpanded.add(bidId);
    }
    setExpandedBids(newExpanded);
  };

  const renderAcceptButton = (bid: BidData) => {
    // TODO: Temporarily disabled individual accept buttons in bid history rows for sellers
    // We may restore this UX later if we want sellers to be able to accept individual bids
    // from the bid history instead of requiring them to use the main Accept button
    return null;

    // if (!canManageBids) return null;
    //
    // // Check if any bid in the deal has already been accepted
    // const hasBidBeenAccepted = bids.some((b) => b.status === 'ACCEPTED');
    // if (hasBidBeenAccepted) return null;
    //
    // // Only show accept button for buyer bids that haven't been accepted yet
    // const canAccept =
    //   bid.bidder === DealRole.BUYER && bid.status !== 'ACCEPTED';
    // if (!canAccept) return null;
    //
    // return (
    //   <Button
    //     onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
    //       e.stopPropagation();
    //       onAcceptBid(bid.bid_id);
    //     }}
    //     type="primary_green"
    //     size="small"
    //     className="px-3 py-1 text-xs font-semibold"
    //     aria-label={`Accept bid ${bid.bid_id}`}
    //   >
    //     Accept
    //   </Button>
    // );
  };

  const groupedBids = groupBidsByBuyer(bids);

  if (!bids || bids.length === 0) {
    return <div className="py-8 text-center text-gray-400">No bids received yet.</div>;
  }

  return (
    <div className="w-full text-white">
      <ul className="space-y-1">
        {groupedBids.map(({ buyerBid, counterOffers }) => {
          const hasCounterOffers = counterOffers.length > 0;
          const isExpanded = expandedBids.has(buyerBid.bid_id);
          const buyerOwnershipBadge = getOwnershipBadge(buyerBid);

          return (
            <React.Fragment key={buyerBid.bid_id}>
              {/* Buyer Bid Row - Custom Layout for Sellers */}
              <li
                className={`grid cursor-pointer grid-cols-[380px_280px_150px_1fr] items-center justify-between px-3 py-2 ${
                  selectedBidId === buyerBid.bid_id
                    ? 'border-l-4 border-blue-500 bg-blue-800'
                    : 'border-l border-gray-800 hover:bg-brand-950'
                }`}
                onClick={() => onSelectBid(buyerBid.bid_id)}
              >
                {/* Bid Description with Expansion */}
                <div className="flex min-w-0 items-center gap-2">
                  {hasCounterOffers && (
                    <IconButton
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        toggleExpanded(buyerBid.bid_id);
                      }}
                      icon={
                        isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />
                      }
                      ariaLabel={isExpanded ? 'Collapse counteroffers' : 'Expand counteroffers'}
                      tooltipText={isExpanded ? 'Collapse counteroffers' : 'Expand counteroffers'}
                      tooltipPosition="top"
                    />
                  )}
                  <span className="text-l font-medium text-white">
                    Bid on {getBidDescription(buyerBid)}
                  </span>
                  {hasCounterOffers && (
                    <span className="ml-2 text-xs text-gray-400">
                      ({counterOffers.length} counteroffer
                      {counterOffers.length !== 1 ? 's' : ''})
                    </span>
                  )}
                </div>

                {/* Price and Ownership - single line */}
                <div className="flex min-w-0 items-center gap-3 whitespace-nowrap">
                  <span className="text-l tracking-tight">Price</span>
                  <span className="text-l text-gray-400">{formatBidPrice(buyerBid)}</span>
                  {buyerOwnershipBadge && (
                    <span className="rounded bg-purple-800 px-2 py-0.5 text-xs text-purple-200">
                      {buyerOwnershipBadge}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {buyerBid.status === 'ACCEPTED' && (
                    <span className="rounded bg-green-600 px-2 py-1 text-xs font-semibold text-green-100">
                      {buyerBid.status}
                    </span>
                  )}
                  {renderAcceptButton(buyerBid)}
                </div>

                {/* Date - single line */}
                <div className="flex min-w-0 items-center justify-end">
                  <span className="whitespace-nowrap text-right text-sm text-gray-400">
                    {formatRelativeDateTime(buyerBid.created_date)}
                  </span>
                </div>
              </li>

              {/* Counter Offers (when expanded) */}
              {isExpanded &&
                counterOffers.map((counterOffer) => (
                  <BidRow
                    key={counterOffer.bid_id}
                    bid={counterOffer}
                    isSelected={selectedBidId === counterOffer.bid_id}
                    isCounterOffer={true}
                    onClick={() => onSelectBid(counterOffer.bid_id)}
                  />
                ))}
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
};
